/** The choice questions of a changeset (plan §1.4 item 4), pure.
 *
 *  An option is RAISED when a change it is `asked_when` is in the draft — instantly from
 *  the catalog (refined by the record kind's `isOptionAsked`), authoritatively from the
 *  preview's `required_choices` once the preview of the current draft is in. A raised
 *  option is answered by the operator, else by its default (edit_context
 *  `option_defaults`, then the catalog's); a `required` one without either blocks Save —
 *  no silent defaults. An answer that `reverts` is no answer: it takes back the changes that
 *  raise the question (`triggerTargets`), and the question goes with them. The update-events
 *  policy is not a question here: it is the ledger's Emit · Suppress switch, and is always
 *  sent. Nor is an entity's event switch (guest_events — the guest group's "None · Emit
 *  user.updated"): `skip` keeps it out, and `choiceExtras` sends it while the draft changes
 *  that entity.
 *
 *  **Confirmed answers.** A `value` option is never asked: it names the value a `choice`
 *  answer confirms (`confirms` — the re-key's `rekey_import_matching_to`, the Custom ID a
 *  Re-key re-keys to). The operator's answer to such a choice is BOUND to the values of the
 *  changes that raised it (`bindAnswer`: each trigger's `to` when it was given) and holds only
 *  while the draft still holds exactly those (`bindingHolds`) — the editor drops it at the
 *  first change, so even a value typed back needs a fresh answer. While it holds, the
 *  confirmed value is sent beside it (`confirmedValues`), in the form the server compares
 *  (`RecordOption.normalize`).
 */
import {
  actionPath,
  draftValuesEqual,
  recordKey,
  type DraftState,
  type DraftTarget,
  type RecordKey,
} from '../draft/changeSet';

import type { RecordOption, RecordRequiredChoice } from './types';

/** The option the Emit · Suppress switch answers. */
export const UPDATE_EVENTS_OPTION = 'update_events';

export type ChoiceAnswers = Record<string, string | boolean>;

export interface RaisedChoice {
  option: RecordOption;
  /** The draft field the question renders under (inline), `"<rec>|<path>"`. */
  anchor: string | null;
  /** The answer the save will use (operator's, else default); null = unanswered. */
  answer: string | boolean | null;
  /** True when the answer is a default the operator has not touched. */
  defaulted: boolean;
  /** Why the preview raised it, in its words; null while the question is estimated from
   *  the catalog. An option that `reasonIsQuestion` is asked in these words. */
  reason: string | null;
}

export function anchorKey(rec: RecordKey, path: string): string {
  return `${rec}|${path}`;
}

/** Draft targets of a trigger key: the field `key`, or the queued action `key`. */
function triggerPaths(key: string): string[] {
  return [key, actionPath(key)];
}

/** The first draft entry (in `asked_when` order) that raises `option`. */
function estimateAnchor(option: RecordOption, state: DraftState, order: RecordKey[]): string | null {
  const recs = [
    ...order.filter((r) => r in state.entries),
    ...Object.keys(state.entries)
      .filter((r) => !order.includes(r))
      .sort(),
  ];
  for (const key of option.asked_when) {
    for (const path of triggerPaths(key)) {
      for (const rec of recs) {
        if (state.entries[rec]?.[path]) return anchorKey(rec, path);
      }
    }
  }
  return null;
}

/** `"booking:4711:date"` → `"booking:4711|date"`; an action key maps to its path. */
export function anchorFromChangeKey(changeKey: string, actionKeys: ReadonlySet<string>): string | null {
  const parts = changeKey.split(':');
  if (parts.length < 3) return null;
  const [entity, id, ...rest] = parts;
  const name = rest.join(':');
  const n = Number(id);
  if (!entity || !Number.isFinite(n) || !name) return null;
  return anchorKey(recordKey(entity, n), actionKeys.has(name) ? actionPath(name) : name);
}

export function optionDefault(
  option: RecordOption,
  optionDefaults: Record<string, unknown> | undefined,
): string | boolean | null {
  const override = optionDefaults?.[option.key];
  if (typeof override === 'string' || typeof override === 'boolean') return override;
  return option.default;
}

export interface RaiseInput {
  options: RecordOption[];
  state: DraftState;
  answers: ChoiceAnswers;
  optionDefaults?: Record<string, unknown>;
  /** Records in ledger order — the anchor prefers the lead. */
  recordOrder?: RecordKey[];
  isOptionAsked?: (option: RecordOption, state: DraftState) => boolean | undefined;
  /** The preview's questions, when the preview of the CURRENT draft is in. */
  previewChoices?: RecordRequiredChoice[] | null;
  /** Action keys that travel as `action:` paths (for anchoring preview triggers). */
  actionKeys?: ReadonlySet<string>;
  /** Options answered by a switch, never asked (an entity's event switch). */
  skip?: ReadonlySet<string>;
}

/** The questions this draft raises, in catalog order. */
export function raisedChoices({
  options,
  state,
  answers,
  optionDefaults,
  recordOrder = [],
  isOptionAsked,
  previewChoices,
  actionKeys = new Set(),
  skip,
}: RaiseInput): RaisedChoice[] {
  const fromPreview = previewChoices ? new Map(previewChoices.map((c) => [c.option, c])) : null;
  const out: RaisedChoice[] = [];
  for (const option of options) {
    if (option.key === UPDATE_EVENTS_OPTION || option.type === 'value' || skip?.has(option.key)) continue;
    let anchor = estimateAnchor(option, state, recordOrder);
    let reason: string | null = null;
    if (fromPreview) {
      const pc = fromPreview.get(option.key);
      if (!pc) continue;
      if (!anchor && pc.triggered_by[0]) anchor = anchorFromChangeKey(pc.triggered_by[0], actionKeys);
      reason = pc.reason?.trim() || null;
    } else {
      if (!anchor) continue;
      if (isOptionAsked?.(option, state) === false) continue;
    }
    const picked = answers[option.key];
    const fallback = optionDefault(option, optionDefaults);
    const answer = picked !== undefined ? picked : fallback;
    out.push({ option, anchor, answer, defaulted: picked === undefined && fallback !== null, reason });
  }
  return out;
}

/** The draft's changes that raise `option` (its `asked_when` fields and actions, on every
 *  record) — what an answer that `reverts` takes back. */
export function triggerTargets(option: RecordOption, state: DraftState): DraftTarget[] {
  const out: DraftTarget[] = [];
  for (const [rec, entries] of Object.entries(state.entries)) {
    for (const key of option.asked_when) {
      for (const path of triggerPaths(key)) {
        if (entries?.[path]) out.push({ rec, path });
      }
    }
  }
  return out;
}

/** One change an answer was given for: where, and the value it held then. */
export interface BoundTrigger extends DraftTarget {
  to: unknown;
}

/** What a confirmed answer holds on to: the changes that raised its question when it was
 *  given, by record and path. */
export type ChoiceBinding = readonly BoundTrigger[];

/** The `value` options of `options` by the key of the choice each confirms. */
export function confirmersOf(options: readonly RecordOption[]): ReadonlyMap<string, RecordOption> {
  const out = new Map<string, RecordOption>();
  for (const o of options) if (o.type === 'value' && o.confirms) out.set(o.confirms, o);
  return out;
}

/** The binding of an answer given to `option` now: its triggers' values. */
export function bindAnswer(option: RecordOption, state: DraftState): ChoiceBinding {
  return triggerTargets(option, state)
    .map(({ rec, path }) => ({ rec, path, to: state.entries[rec]?.[path]?.to }))
    .sort((a, b) => anchorKey(a.rec, a.path).localeCompare(anchorKey(b.rec, b.path)));
}

/** The draft still holds exactly the changes an answer was bound to, with their values. */
export function bindingHolds(binding: ChoiceBinding, option: RecordOption, state: DraftState): boolean {
  const now = bindAnswer(option, state);
  return (
    now.length === binding.length &&
    now.every((t, i) => {
      const b = binding[i]!;
      return t.rec === b.rec && t.path === b.path && draftValuesEqual(t.to, b.to);
    })
  );
}

/** The `value` options to send beside the raised choices the operator answered: each one's
 *  confirmed value — the bound trigger the question is anchored to (else the first), in the
 *  form the server compares. A choice without a holding binding confirms nothing. */
export function confirmedValues(
  raised: readonly RaisedChoice[],
  confirmers: ReadonlyMap<string, RecordOption>,
  bindingOf: (key: string) => ChoiceBinding | undefined,
): { path: string; value: unknown }[] {
  const out: { path: string; value: unknown }[] = [];
  for (const c of raised) {
    const confirmer = confirmers.get(c.option.key);
    if (!confirmer || c.answer === null || c.defaulted) continue;
    const binding = bindingOf(c.option.key);
    const bound = binding?.find((t) => anchorKey(t.rec, t.path) === c.anchor) ?? binding?.[0];
    if (!bound) continue;
    const value = confirmer.normalize ? confirmer.normalize(bound.to) : bound.to;
    out.push({ path: confirmer.path, value: value === undefined ? null : value });
  }
  return out;
}

export function missingChoices(raised: RaisedChoice[]): RaisedChoice[] {
  return raised.filter((c) => c.option.required && c.answer === null);
}

/** What waits for a required question still unanswered — the ledger's Save, the phone's
 *  Render of the draft — in one set of words: `Answer “Re-key import matching”`; null when
 *  every required question is answered. */
export function answerFirst(raised: RaisedChoice[]): string | null {
  const first = missingChoices(raised)[0];
  return first ? `Answer “${first.option.label}”` : null;
}

/** `events` / `options` of the request: the update-events policy, then every raised
 *  question's answer at its `path`, then the switches' (`switches`, path → value), then the
 *  values the answers confirm (`confirmed`, `confirmedValues` — sent even when null: a
 *  confirmed removal). */
export function choiceExtras(
  raised: RaisedChoice[],
  updateEvents: string | null,
  switches: ReadonlyArray<{ path: string; answer: string | boolean }> = [],
  confirmed: ReadonlyArray<{ path: string; value: unknown }> = [],
): { events?: Record<string, unknown>; options?: Record<string, unknown> } {
  const events: Record<string, unknown> = {};
  const opts: Record<string, unknown> = {};
  if (updateEvents) events[UPDATE_EVENTS_OPTION] = updateEvents;
  for (const c of [...raised.map((r) => ({ path: r.option.path, answer: r.answer })), ...switches]) {
    if (c.answer === null) continue;
    const [root, key] = c.path.split('.');
    if (!key) continue;
    if (root === 'events') events[key] = c.answer;
    else if (root === 'options') opts[key] = c.answer;
  }
  for (const c of confirmed) {
    const [root, key] = c.path.split('.');
    if (!key) continue;
    if (root === 'events') events[key] = c.value;
    else if (root === 'options') opts[key] = c.value;
  }
  return {
    ...(Object.keys(events).length ? { events } : {}),
    ...(Object.keys(opts).length ? { options: opts } : {}),
  };
}
